import { apiFetch, loadSession } from "../data/session";

/**
 * Same interface for the real app (API) and the simulator (memory only).
 * Simulated plans carry simulated: true and never reach the server.
 */

const json = (body) => ({ method: "POST", body: JSON.stringify(body || {}) });

export function liveApi(token) {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return {
    simulated: false,
    listChildren: () => apiFetch("/api/family", token).then((data) => (data.students || []).filter((s) => s.status !== "removed")),
    listPlans: (studentId) => apiFetch(`/api/students/${studentId}/plans`, token).then((data) => data.plans),
    createPlan: (body) => apiFetch("/api/plans", token, json(body)),
    editPlan: (id, body) => apiFetch(`/api/plans/${id}`, token, { method: "PATCH", body: JSON.stringify(body) }),
    accept: (id) => apiFetch(`/api/plans/${id}/accept`, token, json()),
    requestChange: (id, note) => apiFetch(`/api/plans/${id}/request-change`, token, json({ note })),
    decline: (id, note) => apiFetch(`/api/plans/${id}/decline`, token, json({ note })),
    cancel: (id, note) => apiFetch(`/api/plans/${id}/cancel`, token, json({ note })),
    checkin: (id, body) => apiFetch(`/api/plans/${id}/checkins`, token, json(body)),
    listCheckins: (id) => apiFetch(`/api/plans/${id}/checkins`, token).then((data) => data.checkins),
    uploadPhoto: async (id, blob) => {
      const form = new FormData();
      form.append("file", blob, "photo.jpg");
      const response = await fetch(`${base}/api/plans/${id}/attachments`, {
        method: "POST",
        headers: { "X-Account-Token": token },
        body: form,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Photo refusée.");
      return data;
    },
    photoUrl: async (attachmentId) => {
      const response = await fetch(`${base}/api/attachments/${attachmentId}`, { headers: { "X-Account-Token": token } });
      if (!response.ok) throw new Error("Photo indisponible.");
      return URL.createObjectURL(await response.blob());
    },
    askHelp: (planId, note) => apiFetch("/api/help", token, json({ planId, note })),
    listHelp: (scope = "active") => apiFetch(`/api/help?scope=${scope}`, token).then((data) => data.requests),
    replyHelp: (id, text) => apiFetch(`/api/help/${id}/reply`, token, json({ text })),
    bookHelp: (id, at, text) => apiFetch(`/api/help/${id}/book`, token, json({ at, text })),
    answerBooking: (id, action, text) => apiFetch(`/api/help/${id}/booking`, token, json({ action, text })),
    transferHelp: (id) => apiFetch(`/api/help/${id}/transfer`, token, json()),
    resolveHelp: (id) => apiFetch(`/api/help/${id}/resolve`, token, json()),
    weeklyReport: (week = "") => apiFetch(`/api/reports/weekly${week ? `?week=${week}` : ""}`, token).then((data) => data.reports),
    pendingReport: () => apiFetch("/api/reports/pending", token).then((data) => data.week),
    markReportSeen: (week) => apiFetch("/api/reports/seen", token, json({ week })),
    weekPlan: (studentId) => apiFetch(`/api/students/${studentId}/week`, token),
    addSession: (body) => apiFetch("/api/sessions", token, json(body)).then((data) => data.week),
    removeSession: (id) => apiFetch(`/api/sessions/${id}`, token, { method: "DELETE" }).then((data) => data.week),
    startSession: (id) => apiFetch(`/api/sessions/${id}/start`, token, json()),
    remindersToday: () => apiFetch("/api/reminders/today", token).then((data) => data.reminders),
    extractCapture: (body) => apiFetch("/api/capture/extract", token, json(body)).then((data) => data.draft),
    confirmCapture: (draftId, items) => apiFetch(`/api/capture/${draftId}/confirm`, token, json({ items })).then((data) => data.result),
    cancelCapture: (draftId) => apiFetch(`/api/capture/${draftId}/cancel`, token, json()),
    proposeSchedule: (studentId, body) => apiFetch(`/api/students/${studentId}/schedule/propose`, token, json(body)),
    confirmSchedule: (studentId, body) => apiFetch(`/api/students/${studentId}/schedule/confirm`, token, json(body)),
    assistant: (studentId, body) => apiFetch("/api/assistant/query", token, json({ studentId, ...body })),
    snoozeSession: (id) => apiFetch(`/api/sessions/${id}/snooze`, token, json()).then((data) => data.week),
    moveSession: (id, body) => apiFetch(`/api/sessions/${id}/move`, token, json(body)).then((data) => data.week),
    startNow: (planId) => apiFetch("/api/sessions/now", token, json({ planId })),
    aiHelpStart: (body) => apiFetch("/api/ai-help", token, json(body)),
    aiHelpTurn: (id, body) => apiFetch(`/api/ai-help/${id}/turn`, token, json(body)),
    aiHelpOutcome: (id, outcome) => apiFetch(`/api/ai-help/${id}/outcome`, token, json({ outcome })),
  };
}

// ---------- simulation (simulator only) ----------

export const setSimForfait = (level) => {
  sim.forfait = level;
};

const sim = { forfait: 3, plans: [], checkins: [], photos: {}, help: [], sessions: [], outbox: [], deadlines: [], drafts: {}, proposals: [], aiHelp: {}, clockOffset: 0, seenWeek: "" };

/** Copy of the simulated data, so the simulator can reopen where it was left. */
export const simSnapshot = () => JSON.parse(JSON.stringify(sim));
export const simRestore = (data) => {
  if (data && typeof data === "object") Object.assign(sim, data);
};

/** Simulated clock: the simulator can move time forward to show reminders and the weekly review. */
export const simClock = {
  now: () => new Date(Date.now() + sim.clockOffset),
  advance: (ms) => {
    sim.clockOffset += ms;
    return simClock.now();
  },
  reset: () => {
    sim.clockOffset = 0;
  },
};

const isoNow = () => simClock.now().toISOString();

export function mondayOf(day) {
  const d = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
}

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function simReport(monday, studentName) {
  const start = monday.getTime();
  const end = start + 7 * 86400000;
  const rows = sim.checkins.filter((row) => {
    const at = new Date(row.createdAt).getTime();
    return at >= start && at < end;
  });
  const difficulty = { easy: 0, ok: 0, hard: 0 };
  rows.forEach((row) => {
    difficulty[row.difficulty || "ok"] += 1;
  });
  const plans = sim.plans.filter((plan) => plan.status === "active" || rows.some((row) => row.planId === plan.id));
  const help = sim.help.filter((item) => {
    const at = new Date(item.createdAt).getTime();
    return at >= start && at < end;
  });
  const sunday = new Date(start + 6 * 86400000);
  return {
    week: { start: ymd(monday), end: ymd(sunday) },
    student: { id: "sim-student", name: studentName },
    sessions: rows.length,
    minutes: rows.reduce((sum, row) => sum + (row.actualMinutes || 0), 0),
    plannedMinutes: rows.reduce((sum, row) => sum + (sim.plans.find((plan) => plan.id === row.planId)?.suggestedMinutes || 20), 0),
    difficulty,
    plans: plans.map((plan) => ({
      id: plan.id,
      title: plan.title,
      status: plan.status,
      doneThisWeek: rows.filter((row) => row.planId === plan.id).length,
      perWeek: Math.max(1, Math.round((plan.targetCount || 1) / (plan.repeatWeeks || 1))),
    })),
    checkins: rows.map((row) => ({
      at: row.createdAt,
      planTitle: sim.plans.find((plan) => plan.id === row.planId)?.title || "",
      minutes: row.actualMinutes,
      difficulty: row.difficulty || "ok",
      summary: row.summary || "",
      photos: (row.attachmentIds || []).length,
    })),
    help: { opened: help.length, resolved: help.filter((item) => item.status === "resolved").length },
    empty: !rows.length,
  };
}

/** Demo data for the check-in (Flow 24) when the simulator is empty: 3 deadlines, 3 active plans, 2 séances. */
export function simSeedCheckin(studentName = "Élève") {
  if (sim.plans.some((plan) => plan.status === "active")) return;
  const day = (n) => ymd(new Date(simClock.now().getTime() + n * 86400000));
  const now = new Date().toISOString();
  const rows = [
    ["Maths — Examen chapitre 3", "Maths", "Examen chapitre 3", "exam", 4, 3],
    ["Français — Devoir de lecture", "Français", "Devoir de lecture", "assignment", 2, 2],
    ["Sciences — Projet", "Sciences", "Projet", "assignment", 9, 2],
  ];
  rows.forEach(([title, subject, label, type, inDays, targetCount], index) => {
    const id = `sim-seed-${Date.now()}-${index}`;
    sim.plans.push({
      id, studentId: "sim-student", createdBy: "sim-student", creatorRole: "student", creatorName: studentName, studentName,
      originType: "self", status: "active", version: 1, createdAt: now, updatedAt: now, simulated: true,
      number: sim.plans.length + 1, title, targetCount, suggestedMinutes: 20, dueDate: day(inDays), repeatWeeks: 1,
    });
    sim.deadlines.push({ id: `simdl-seed-${index}`, planId: id, subject, title: label, type, dueDate: day(inDays) });
    if (index < 2) sim.sessions.push({ id: `sims-seed-${index}`, groupId: `sims-seed-${index}`, block: 1, blocks: 1, planId: id, studentId: "sim-student", date: day(1), time: index ? "16:20" : "16:00", status: "planned", simulated: true });
  });
}

/** Counters for the simulator side panel. */
export function simStats() {
  const plans = sim.plans.filter((plan) => ["active", "completed"].includes(plan.status));
  const target = plans.reduce((sum, plan) => sum + (plan.targetCount || 1), 0);
  const done = plans.reduce((sum, plan) => sum + Math.min(sim.checkins.filter((row) => row.planId === plan.id).length, plan.targetCount || 1), 0);
  const monday = mondayOf(simClock.now()).getTime();
  const days = new Set(
    sim.checkins.filter((row) => new Date(row.createdAt).getTime() >= monday).map((row) => new Date(row.createdAt).toDateString())
  );
  return {
    done,
    target,
    minutes: sim.checkins.reduce((sum, row) => sum + (row.actualMinutes || 0), 0),
    regularity: Math.round((days.size / 7) * 100),
    title: plans[plans.length - 1]?.title || "—",
    activePlans: sim.plans.filter((plan) => plan.status === "active"),
    checkinsToday: sim.checkins.filter((row) => new Date(row.createdAt).toDateString() === simClock.now().toDateString()).length,
  };
}

function simHelpSet(id, actor, kind, text, updates = {}) {
  const item = sim.help.find((row) => row.id === id);
  if (!item) throw new Error("Demande d’aide introuvable (simulation).");
  const at = isoNow();
  Object.assign(item, updates, { updatedAt: at });
  item.thread = [...item.thread, { at, by: actor, role: actor === "sim-parent" ? "parent" : "student", kind, text }];
  return Promise.resolve({ ...item });
}

function simWeekRows(planId) {
  const today = simClock.now();
  const monday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - ((today.getDay() + 6) % 7));
  return sim.checkins.filter((row) => row.planId === planId && new Date(row.createdAt) >= monday);
}

function simPresent(plan) {
  const rows = sim.checkins.filter((row) => row.planId === plan.id);
  const target = plan.targetCount || 1;
  const done = Math.min(plan.recurring ? simWeekRows(plan.id).length : rows.length, target);
  return {
    ...plan,
    progress: { done, target, ratio: done / target },
    declaredMinutes: rows.reduce((sum, row) => sum + (row.actualMinutes || 0), 0),
    helpRequests: rows.filter((row) => row.needsHelp).length,
    lastCheckin: rows[rows.length - 1] || null,
  };
}

function simFind(id) {
  const plan = sim.plans.find((item) => item.id === id);
  if (!plan) throw new Error("Engagement introuvable (simulation).");
  return plan;
}

function simSet(id, updates) {
  const plan = simFind(id);
  Object.assign(plan, updates, { updatedAt: new Date().toISOString() });
  return Promise.resolve(simPresent(plan));
}

const OPEN = ["pending_acceptance", "change_requested", "active"];

// ---------- Flow 16 / 17 in the simulator: week planning and reminder tasks ----------
const perWeek = (plan) => Math.max(1, Math.round((plan.targetCount || 1) / (plan.repeatWeeks || 1)));
const OFFSETS = [0, 60, 180];
const SMS_TEXT = [
  "Plan Étude : c’est l’heure de ta séance « {title} ». Commence ici : {link}",
  "Plan Étude : ta séance « {title} » t’attend. Commence ici : {link}",
  "Plan Étude : dernier rappel pour aujourd’hui, « {title} ». {link}",
];

function simWeek() {
  const now = simClock.now();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monday = mondayOf(today);
  if (today.getDay() === 0) monday.setDate(monday.getDate() + 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const first = today > monday ? today : monday;
  const days = [];
  for (let d = new Date(first); d <= sunday; d.setDate(d.getDate() + 1)) days.push(ymd(d));
  const inWeek = (iso) => iso >= ymd(monday) && iso <= ymd(sunday);
  const active = sim.plans.filter((plan) => plan.status === "active");
  const sessions = sim.sessions.filter((row) => inWeek(row.date) && row.status !== "cancelled");
  const end = new Date(sunday);
  end.setDate(end.getDate() + 1);
  const plans = active.map((plan) => {
    const done = sim.checkins.filter((row) => {
      const at = new Date(row.createdAt);
      return row.planId === plan.id && at >= monday && at < end;
    }).length;
    const upcoming = new Set(
      sessions
        .filter((row) => row.planId === plan.id && (row.status === "started" || (row.status === "planned" && row.date >= ymd(today))))
        .map((row) => row.groupId || row.id)
    ).size;
    const leftTotal = plan.recurring ? perWeek(plan) : Math.max(0, (plan.targetCount || 1) - sim.checkins.filter((row) => row.planId === plan.id).length);
    return { id: plan.id, title: plan.title, dueDate: plan.dueDate || null, perWeek: perWeek(plan), doneThisWeek: done, toPlace: Math.max(0, Math.min(perWeek(plan) - done, leftTotal) - upcoming) };
  });
  const titles = Object.fromEntries(active.map((plan) => [plan.id, plan.title]));
  return {
    week: { start: ymd(monday), end: ymd(sunday) },
    today: ymd(today),
    days,
    plans,
    sessions: sessions.filter((row) => titles[row.planId]).map((row) => ({ ...row, planTitle: titles[row.planId] })).sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`)),
  };
}

export function priorityPlan(plans) {
  const waiting = plans.filter((plan) => plan.doneThisWeek < plan.perWeek);
  return waiting.sort((a, b) => (a.dueDate || "9999-12-31").localeCompare(b.dueDate || "9999-12-31") || (a.doneThisWeek - a.perWeek) - (b.doneThisWeek - b.perWeek))[0] || null;
}

function simTasks(day) {
  const rows = sim.sessions.filter((row) => row.date === day && row.status !== "cancelled");
  const tasks = rows
    .filter((row) => row.status === "planned" && (row.block || 1) === 1)
    .map((row) => ({ key: `s:${row.id}`, planId: row.planId, sessionId: row.id, time: row.time, started: row.status === "started" }));
  if (!rows.length && !sim.checkins.some((row) => ymd(new Date(row.createdAt)) === day)) {
    const plan = priorityPlan(simWeek().plans);
    if (plan) tasks.push({ key: `p:${day}`, planId: plan.id, sessionId: null, time: "16:00", started: false });
  }
  return tasks;
}

/** Reminders that became due between two simulated instants (8 h to 21 h, max 3 per task). */
export function simReminders(from, to) {
  const out = [];
  const day = ymd(to);
  simTasks(day).forEach((task) => {
    const [h, m] = task.time.split(":").map(Number);
    OFFSETS.forEach((offset, index) => {
      const n = index + 1;
      const due = new Date(to.getFullYear(), to.getMonth(), to.getDate(), h, m + offset);
      if (due <= from || due > to || due.getHours() < 8 || due.getHours() >= 21) return;
      if (to.getTime() - due.getTime() >= 3600000) return;
      if (n > 1 && task.started) return;
      if (sim.outbox.some((row) => row.taskKey === task.key && row.n === n)) return;
      const title = sim.plans.find((plan) => plan.id === task.planId)?.title || "ton plan d’étude";
      const link = `${window.location.origin}/?${task.sessionId ? `seance=${task.sessionId}` : `plan=${task.planId}`}`;
      const row = { id: `sms-${Date.now()}-${n}`, taskKey: task.key, n, planId: task.planId, sessionId: task.sessionId, link, text: SMS_TEXT[index].replace("{title}", title).replace("{link}", link), at: to.toISOString(), status: "simulated" };
      sim.outbox.push(row);
      out.push(row);
    });
  });
  return out;
}

/** Next reminder instant after now (simulator "next reminder" button). */
export function simNextReminder() {
  const now = simClock.now();
  for (let add = 0; add < 8; add += 1) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + add);
    const day = ymd(d);
    const dues = simTasks(day)
      .flatMap((task) => {
        const [h, m] = task.time.split(":").map(Number);
        return OFFSETS.map((offset, index) => ({ n: index + 1, task, at: new Date(d.getFullYear(), d.getMonth(), d.getDate(), h, m + offset) }));
      })
      .filter(({ n, task, at }) => at > now && at.getHours() >= 8 && at.getHours() < 21 && !(n > 1 && task.started) && !sim.outbox.some((row) => row.taskKey === task.key && row.n === n))
      .sort((a, b) => a.at - b.at);
    if (dues.length) return dues[0].at;
  }
  return null;
}

export const simSmsToday = () => sim.outbox.filter((row) => ymd(new Date(row.at)) === ymd(simClock.now())).length;

function simSessionMark(planId, sessionId) {
  const today = ymd(simClock.now());
  const target = sessionId
    ? sim.sessions.find((row) => row.id === sessionId && ["planned", "started"].includes(row.status))
    : sim.sessions.filter((row) => row.planId === planId && row.date === today && ["planned", "started"].includes(row.status)).sort((a, b) => a.time.localeCompare(b.time))[0];
  if (target) sim.sessions.filter((item) => (item.groupId || item.id) === (target.groupId || target.id)).forEach((item) => { item.status = "done"; });
}

export function simApi({ parentName = "Parent", studentName = "Élève", role = "parent" } = {}) {
  const actorId = role === "parent" ? "sim-parent" : "sim-student";
  return {
    simulated: true,
    listChildren: async () => [{ id: "sim-student", name: studentName, status: "active", simulated: true }],
    listPlans: async () => sim.plans.filter((plan) => plan.studentId === "sim-student").map(simPresent).reverse(),
    createPlan: async (body) => {
      const self = role !== "parent";
      // Same rule as the server: Essentiel = 1 open plan per creator; Famille 25 and Famille Plus 50 in total.
      const open = sim.plans.filter((plan) => OPEN.includes(plan.status));
      const blocked = sim.forfait >= 2 ? open.length >= (sim.forfait === 2 ? 25 : 50) : open.some((plan) => plan.createdBy === actorId);
      if (blocked) {
        const error = new Error(
          self
            ? "Tu as déjà un plan d’étude personnel en cours. Termine-le avant d’en créer un autre. Seuls les forfaits Plan Étude Famille et Famille Plus permettent plus d’un plan d’étude à la fois."
            : "Un engagement est déjà en cours avec cet élève. Il doit le terminer avant d’en proposer un autre. Seuls les forfaits Plan Étude Famille et Famille Plus permettent plus d’un plan d’étude à la fois."
        );
        error.status = 409;
        throw error;
      }
      const now = new Date().toISOString();
      const plan = {
        id: `sim-${Date.now()}`,
        studentId: "sim-student",
        createdBy: actorId,
        creatorRole: self ? "student" : "parent",
        creatorName: self ? studentName : parentName,
        studentName,
        originType: self ? "self" : "parent_proposal",
        status: self ? "active" : "pending_acceptance",
        version: 1,
        createdAt: now,
        updatedAt: now,
        simulated: true,
        number: sim.plans.filter((item) => item.studentId === "sim-student").length + 1,
        ...body,
      };
      sim.plans.push(plan);
      return simPresent(plan);
    },
    editPlan: async (id, body) => simSet(id, { ...body, status: "pending_acceptance", changeRequest: null, version: (simFind(id).version || 1) + 1 }),
    accept: async (id) => simSet(id, { status: "active", acceptedAt: new Date().toISOString() }),
    requestChange: async (id, note) => simSet(id, { status: "change_requested", changeRequest: { note } }),
    decline: async (id, note) => simSet(id, { status: "declined", changeRequest: note ? { note } : null }),
    cancel: async (id) => simSet(id, { status: "cancelled" }),
    checkin: async (id, body) => {
      const plan = simFind(id);
      if (plan.status !== "active") throw new Error("Cet engagement n'est pas actif.");
      const existing = sim.checkins.find((row) => row.planId === id && row.idempotencyKey === body.idempotencyKey);
      if (!existing) sim.checkins.push({ id: `simc-${Date.now()}`, planId: id, createdAt: isoNow(), ...body });
      if (!existing) simSessionMark(id, body.sessionId);
      if (!plan.recurring && sim.checkins.filter((row) => row.planId === id).length >= (plan.targetCount || 1)) {
        plan.status = "completed";
        plan.completedAt = new Date().toISOString();
      }
      return { plan: simPresent(plan), duplicate: Boolean(existing) };
    },
    listCheckins: async (id) => sim.checkins.filter((row) => row.planId === id),
    uploadPhoto: async (_id, blob) => {
      const photoId = `simp-${Date.now()}`;
      sim.photos[photoId] = URL.createObjectURL(blob);
      return { id: photoId };
    },
    photoUrl: async (attachmentId) => sim.photos[attachmentId] || "",
    askHelp: async (planId, note) => {
      const plan = simFind(planId);
      const at = isoNow();
      const item = {
        id: `simh-${Date.now()}`,
        planId,
        planTitle: plan.title,
        studentId: "sim-student",
        studentName,
        note,
        status: "open",
        booking: null,
        createdAt: at,
        updatedAt: at,
        simulated: true,
        thread: [{ at, by: "sim-student", role: "student", kind: "ask", text: note }],
      };
      sim.help.push(item);
      return { ...item };
    },
    listHelp: async (scope = "active") =>
      sim.help.filter((item) => scope === "all" || item.status !== "resolved").map((item) => ({ ...item })).reverse(),
    replyHelp: (id, text) =>
      simHelpSet(id, actorId, "reply", text, sim.help.find((row) => row.id === id)?.status === "open" ? { status: "answered" } : {}),
    bookHelp: (id, at, text) => simHelpSet(id, actorId, "book", text, { status: "booked", booking: { at, status: "proposed", note: text } }),
    answerBooking: (id, action, text) => {
      const item = sim.help.find((row) => row.id === id);
      return simHelpSet(id, actorId, `booking_${action}`, text, {
        booking: { ...(item?.booking || {}), status: action === "accept" ? "accepted" : "change_requested", answer: text },
      });
    },
    transferHelp: (id) => simHelpSet(id, actorId, "transfer", "", { status: "transferred" }),
    resolveHelp: (id) => simHelpSet(id, actorId, "resolve", "", { status: "resolved" }),
    weeklyReport: async (week = "") => [simReport(week ? mondayOf(new Date(`${week}T12:00:00`)) : mondayOf(simClock.now()), studentName)],
    pendingReport: async () => {
      const last = mondayOf(simClock.now());
      last.setDate(last.getDate() - 7);
      const week = ymd(last);
      return sim.plans.length && sim.seenWeek < week ? week : null;
    },
    markReportSeen: async (week) => {
      sim.seenWeek = week;
      return { week };
    },
    weekPlan: async () => ({ ...simWeek(), availability: sim.availability || null }),
    addSession: async ({ planId, date, time }) => {
      const state = simWeek();
      const info = state.plans.find((plan) => plan.id === planId);
      if (!info) throw new Error("Ce plan d’étude n’est pas en cours.");
      if (!state.days.includes(date)) throw new Error("Choisis un jour entre aujourd’hui et dimanche.");
      if (new Date(`${date}T${time}:00`).getTime() < simClock.now().getTime() - 20 * 60000) throw new Error("Cette heure est déjà passée.");
      if (info.toPlace <= 0) throw new Error("Toutes les séances de ce plan sont déjà placées cette semaine.");
      if (state.sessions.some((row) => row.date === date && row.time === time && ["planned", "started"].includes(row.status))) {
        throw new Error("Tu as déjà une séance à cette heure-là.");
      }
      const blocks = Math.max(1, Math.min(3, Math.ceil((simFind(planId)?.suggestedMinutes || 20) / 20)));
      const groupId = `sims-${Date.now()}`;
      for (let index = 0; index < blocks; index += 1) {
        const minute = toMin(time) + index * 20;
        if (sim.sessions.some((row) => row.date === date && row.time === hhmm(minute) && ["planned", "started"].includes(row.status))) {
          sim.sessions = sim.sessions.filter((row) => row.groupId !== groupId);
          throw new Error("Tu as déjà une séance à cette heure-là.");
        }
        sim.sessions.push({ id: index ? `${groupId}-${index + 1}` : groupId, groupId, block: index + 1, blocks, planId, studentId: "sim-student", date, time: hhmm(minute), status: "planned", simulated: true });
      }
      return simWeek();
    },
    removeSession: async (id) => {
      const row = sim.sessions.find((item) => item.id === id);
      if (row && row.status === "planned") sim.sessions.filter((item) => (item.groupId || item.id) === (row.groupId || row.id)).forEach((item) => { item.status = "cancelled"; });
      return simWeek();
    },
    startSession: async (id) => {
      const row = sim.sessions.find((item) => item.id === id);
      if (!row) throw new Error("Séance introuvable (simulation).");
      if (row.status === "planned") sim.sessions.filter((item) => (item.groupId || item.id) === (row.groupId || row.id)).forEach((item) => { item.status = "started"; });
      return { session: { ...row }, plan: simPresent(simFind(row.planId)) };
    },
    remindersToday: async () => sim.outbox.filter((row) => ymd(new Date(row.at)) === ymd(simClock.now())),
    // MVP flows in the simulator: AI calls go to the lab endpoints (nothing stored), writes stay in memory.
    extractCapture: async ({ text, image, audience }) => {
      const result = await labPost("/api/lab/capture-extract", { text, image, audience, today: ymd(simClock.now()) });
      const open = sim.plans.filter((plan) => OPEN.includes(plan.status));
      const items = result.items.map((item) => {
        const hit = open.find((plan) => plan.title.toLowerCase().includes(item.subject.toLowerCase()) && (!item.dueDate || plan.dueDate === item.dueDate));
        return { ...item, match: hit ? { planId: hit.id, title: hit.title } : null };
      });
      return { id: `simd-${Date.now()}`, ...result, items, simulated: true };
    },
    confirmCapture: async function confirmCapture(draftId, items) {
      if (sim.drafts[draftId]) return sim.drafts[draftId];
      const created = [];
      const attached = [];
      const failed = [];
      for (const item of items) {
        if (item.action === "skip") continue;
        if (item.action === "attach" && item.planId) {
          attached.push({ planId: item.planId, title: simFind(item.planId)?.title });
        } else {
          try {
            const title = item.subject && !item.title.includes(item.subject) ? `${item.subject} — ${item.title}` : item.title;
            const plan = await this.createPlan({ title, instructions: item.description || "", targetCount: item.blocks || 2, suggestedMinutes: 20, dueDate: item.dueDate || null, repeatWeeks: 1 });
            created.push(plan);
            item.planId = plan.id;
          } catch (problem) {
            failed.push({ title: item.title, reason: problem.message });
            continue;
          }
        }
        if (item.dueDate) sim.deadlines.push({ id: `simdl-${Date.now()}-${item.key}`, planId: item.planId, subject: item.subject, title: item.title, type: item.type, dueDate: item.dueDate });
      }
      sim.drafts[draftId] = { created, attached, failed };
      return sim.drafts[draftId];
    },
    cancelCapture: async () => ({ cancelled: true }),
    proposeSchedule: async (_studentId, body) => {
      sim.availability = { kind: body.availability, start: body.start, end: body.end };
      return simPropose(body);
    },
    confirmSchedule: async function confirmSchedule(_studentId, { proposalId, slots }) {
      if (sim.proposals.includes(proposalId)) return { created: 0, replayed: true, week: simWeek() };
      sim.proposals.push(proposalId);
      let created = 0;
      for (const slot of slots) {
        try {
          await this.addSession(slot);
          created += 1;
        } catch {
          // a slot that no longer fits is skipped
        }
      }
      return { created, replayed: false, week: simWeek() };
    },
    assistant: async (_studentId, { intent, text }) => simAssistant(intent, text),
    snoozeSession: async (id) => {
      const row = sim.sessions.find((item) => item.id === id);
      if (!row || row.status !== "planned") throw new Error("Cette séance est déjà commencée.");
      const now = simClock.now();
      const planned = new Date(`${row.date}T${row.time}:00`);
      const base = planned > now ? planned : now;
      const next = new Date(base.getTime() + (20 - (base.getMinutes() % 20)) * 60000);
      next.setSeconds(0, 0);
      const clock = (d) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      while (ymd(next) === row.date && sim.sessions.some((item) => item.id !== id && item.date === row.date && item.time === clock(next) && ["planned", "started"].includes(item.status))) {
        next.setMinutes(next.getMinutes() + 20);
      }
      if (ymd(next) !== row.date || next.getHours() >= 21) throw new Error("Trop tard pour aujourd’hui : choisis « Reporter ».");
      row.time = `${String(next.getHours()).padStart(2, "0")}:${String(next.getMinutes()).padStart(2, "0")}`;
      return simWeek();
    },
    moveSession: async (id, { date, time }) => {
      const row = sim.sessions.find((item) => item.id === id);
      if (!row || row.status !== "planned") throw new Error("Seule une séance prévue et non commencée peut être déplacée.");
      if (!simWeek().days.includes(date)) throw new Error("Choisis un jour entre aujourd’hui et dimanche.");
      if (new Date(`${date}T${time}:00`) < simClock.now()) throw new Error("Cette heure est déjà passée.");
      if (sim.sessions.some((item) => item.id !== id && item.date === date && item.time === time && ["planned", "started"].includes(item.status))) throw new Error("Tu as déjà une séance à cette heure-là.");
      Object.assign(row, { date, time });
      return simWeek();
    },
    startNow: async (planId) => {
      const now = simClock.now();
      const row = { id: `sims-${Date.now()}`, planId, studentId: "sim-student", date: ymd(now), time: `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`, status: "started", simulated: true };
      sim.sessions.push(row);
      return { session: row, plan: simPresent(simFind(planId)) };
    },
    aiHelpStart: async ({ planId, text, image }) => {
      const plan = simFind(planId);
      const turn = await labPost("/api/lab/ai-help", { text, image, planTitle: plan?.title || "" });
      const id = `simai-${Date.now()}`;
      sim.aiHelp[id] = { id, planId, attempts: 1, image, history: [{ role: "user", content: text || "(photo)" }, { role: "assistant", content: turn.message }], outcome: null };
      return { session: { id, planId, attempts: 1 }, turn, triesLeft: 2 };
    },
    aiHelpTurn: async (id, { text, again }) => {
      const doc = sim.aiHelp[id];
      if (!doc || doc.attempts >= 3) throw new Error("On a fait 3 essais. Tu peux demander l’aide d’un tuteur ou revenir à ton plan.");
      const turn = await labPost("/api/lab/ai-help", { text, image: doc.image, planTitle: simFind(doc.planId)?.title || "", again, history: doc.history });
      doc.attempts += 1;
      doc.history = [...doc.history, { role: "user", content: text || "Explique autrement." }, { role: "assistant", content: turn.message }].slice(-8);
      return { turn, triesLeft: 3 - doc.attempts };
    },
    aiHelpOutcome: async function aiHelpOutcome(id, outcome) {
      const doc = sim.aiHelp[id];
      if (!doc) throw new Error("Aide introuvable.");
      if (doc.outcome) return { session: doc, help: null, replayed: true };
      doc.outcome = outcome;
      doc.image = null;
      const help = outcome === "human_help_requested" ? await this.askHelp(doc.planId, `Aide IA (${doc.attempts} essai(s)) · ${doc.history[0]?.content || ""}`.slice(0, 480)) : null;
      return { session: doc, help, replayed: false };
    },
  };
}

async function labPost(path, body) {
  const token = loadSession()?.token || "";
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...(token ? { "X-Account-Token": token } : {}) },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.detail || "L’IA ne répond pas (simulateur).");
  return data;
}

const WINDOWS = { after_school: ["16:00", "18:00"], evening: ["18:00", "21:00"] };
const hhmm = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const toMin = (value) => Number(value.slice(0, 2)) * 60 + Number(value.slice(3, 5));

function simPropose({ availability, start, end }) {
  const [from, to] = availability === "custom" ? [start, end] : WINDOWS[availability];
  if (!from || !to || from >= to) throw new Error("Choisis une heure de début et de fin (pas de 20 min).");
  const state = simWeek();
  const now = simClock.now();
  const taken = new Set(state.sessions.filter((row) => ["planned", "started"].includes(row.status)).map((row) => `${row.date} ${row.time}`));
  const slots = [];
  const unplaced = [];
  [...state.plans].sort((a, b) => (a.dueDate || "9999").localeCompare(b.dueDate || "9999") || b.toPlace - a.toPlace).forEach((plan) => {
    let need = plan.toPlace;
    const used = new Set(state.sessions.filter((row) => row.planId === plan.id && ["planned", "started"].includes(row.status)).map((row) => row.date));
    state.days.forEach((day) => {
      if (need <= 0 || used.has(day) || (plan.dueDate && day >= plan.dueDate)) return;
      for (let minute = toMin(from); minute + 20 <= toMin(to); minute += 20) {
        const time = hhmm(minute);
        if (new Date(`${day}T${time}:00`) <= now || taken.has(`${day} ${time}`)) continue;
        slots.push({ planId: plan.id, planTitle: plan.title, date: day, time, blocks: 1, dueDate: plan.dueDate });
        taken.add(`${day} ${time}`);
        used.add(day);
        need -= 1;
        break;
      }
    });
    if (need > 0) unplaced.push({ planId: plan.id, title: plan.title, count: need, reason: plan.dueDate ? "Pas assez de jours avant l’échéance." : "Pas assez de place dans tes disponibilités." });
  });
  return { proposalId: `simp-${Date.now()}`, slots, unplaced, window: { start: from, end: to } };
}

export const ASSISTANT_RULES = [
  ["START_20_MIN_SESSION", /\b(20|vingt) ?min.*(maintenant|tout de suite)|j ?ai (20|vingt) ?min/],
  ["RESCHEDULE_SESSION", /deplac|reporter|changer (ma|la) seance|decaler/],
  ["GET_NEXT_SESSION", /prochaine seance|ma seance|quand.*seance/],
  ["GET_WEEK_SCHEDULE", /horaire|semaine|calendrier/],
  ["LIST_UPCOMING_DEADLINES", /echeance|examen|remise|a remettre|controle/],
  ["LIST_ACTIVE_PLANS", /plans? (en cours|actifs?)|qu est ce que j ai|mes plans/],
  ["REQUEST_HUMAN_HELP", /tuteur|parler a quelqu/],
  ["REQUEST_AI_HELP", /je bloque|comprends pas|aide moi|explique/],
  ["CAPTURE_SCHOOLWORK", /organise|j ai un (examen|devoir|controle)|photo de mon agenda/],
];

export function matchIntent(text) {
  const folded = (text || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  return ASSISTANT_RULES.find(([, pattern]) => pattern.test(folded))?.[0] || null;
}

function simNextSession() {
  const now = simClock.now();
  const today = ymd(now);
  const clock = hhmm(now.getHours() * 60 + now.getMinutes() - 20);
  return sim.sessions
    .filter((row) => ["planned", "started"].includes(row.status) && simFind(row.planId)?.status === "active" && (row.date > today || (row.date === today && (row.status === "started" || row.time >= clock))))
    .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
    .map((row) => ({ ...row, planTitle: simFind(row.planId)?.title }))[0] || null;
}

function simAssistant(intent, text) {
  const picked = intent || matchIntent(text) || "UNKNOWN";
  const state = simWeek();
  const data = {};
  if (picked === "LIST_ACTIVE_PLANS") data.plans = state.plans;
  if (picked === "LIST_UPCOMING_DEADLINES") {
    const today = ymd(simClock.now());
    data.deadlines = sim.deadlines.filter((row) => row.dueDate >= today).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }
  if (picked === "GET_NEXT_SESSION" || picked === "RESCHEDULE_SESSION") data.session = simNextSession();
  if (picked === "GET_WEEK_SCHEDULE") Object.assign(data, { sessions: state.sessions, week: state.week });
  if (picked === "START_20_MIN_SESSION") {
    const next = simNextSession();
    data.session = next && next.date === ymd(simClock.now()) ? next : null;
    data.plan = data.session ? null : priorityPlan(state.plans) || state.plans[0] || null;
  }
  return { intent: picked, routedBy: intent ? "button" : picked === "UNKNOWN" ? "none" : "rules", data };
}

/** Shrink a phone photo before upload (max 1600 px, JPEG). */
export function compressPhoto(file, maxSide = 1600) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", 0.82);
      };
      img.onerror = () => resolve(file);
      img.src = reader.result;
    };
    reader.onerror = () => resolve(file);
    reader.readAsDataURL(file);
  });
}

export function newKey() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `k-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
